import TopicAdapter from '@javascripts/adapters/topic_adapter';
import URLUtils from '@javascripts/utils/url_utils';

export default class MessageManager {
  constructor({ tech, data } = {}) {
    this._tech = tech;
    this._data = data;
  }

  tech() {
    return this._tech;
  }

  data() {
    return this._data;
  }

  ads() {
    return this.tech().plugin().adsObject();
  }

  ima() {
    return this.tech().plugin().imaObject();
  }

  lastMutedCondition(){
    return this._lastMutedCondition;
  }

  playlists() {
    return this.tech().plugin().hlsObject().hls().playlists;
  }

  shouldInit() {
    return this.data().embed() === 'true';
  }

  commands() {
    return {
      'vidio.playback.mute': this.commandMute.bind(this),
      'vidio.playback.unmute': this.commandUnmute.bind(this),
      'vidio.playback.play': this.commandPlay.bind(this),
      'vidio.playback.pause': this.commandPause.bind(this),
      'vidio.playback.ui.hide_fullscreen': this.commandHideFullscreenButton.bind(this),
      'vidio.playback.ui.hide_topbar': this.commandHideTopBar.bind(this),
      'vidio.playback.disable_pause': this.commandDisablePause.bind(this),
      'vidio.playback.ui.hide_components': this.commandUiHideComponentsAll.bind(this),
      'vidio.playback.ui.hide_components_show_logo': this.commandUiHideComponentsShowLogo.bind(this),
      'vidio.playback.ui.show_components': this.commandUiShowComponents.bind(this),
      'vidio.playback.ui.show_play_components_only': this.commandUiShowPlayComponentsOnly.bind(this),
      'vidio.playback.ui.hide_vidio_logo': this.commandUiHideVidioLogo.bind(this),
      'vidio.playback.ui.cancel_hide_vidio_logo': this.commandUiCancelHideVidioLogo.bind(this)
    };
  }

  init() {
    this.commandSendEndEvent();
    this.commandSendStartEvent();
    this.bindMessageListener();
    this.commandSendPlayEvent();
    this.commandSendStreamEndEvent();
    this.bindLivestreamingCCU();
    this.commandSendOnPlayEvent();
    this.commandSendOnPauseEvent();
    this.commandSendOnMuteEvent();
    this.commandSendOnUnmuteEvent();
    this.commandSendOnAdStartEvent();
    this.commandSendOnAdCompleteEvent();

    if(this.data()){
      this._lastMutedCondition = this.data().mute();
    }
  }

  bindMessageListener() {
    window.addEventListener('message', (e) => {
      const command = this.commands()[e.data];

      if (typeof command === 'function') {
        command();
      }
    });
  }

  commandMute() {
    this.tech().muted(true);
  }

  commandUnmute() {
    this.tech().muted(false);
  }

  commandPlay() {
    if (typeof this.ads().inAdBreak === 'function' && this.ads().inAdBreak()) {
      this.ima().resumeAd();
    } else {
      this.tech().play();
    }
  }

  commandPause() {
    if (typeof this.ads().inAdBreak === 'function' && this.ads().inAdBreak()) {
      this.ima().pauseAd();
    } else {
      this.tech().pause();
    }
  }

  commandHideFullscreenButton() {
    const buttonFullscreen = this.tech().el().querySelector('.vjs-fullscreen-control');
    buttonFullscreen.addClass('hidden-on-message');
  }

  commandHideTopBar() {
    const topbar = this.tech().el().querySelector('.vjs-top-bar');
    topbar.addClass('hidden-on-message');
  }

  commandDisablePause() {
    this.tech().on('pause', () => {
      this.tech().play();
    });
    const buttonPause = this.tech().el().querySelector('.vjs-big-pause-button');
    buttonPause.addClass('hidden-on-message');
  }

  commandUiHideComponentsShowLogo() {
    const className = ['embed-ui-hide-components', 'embed-ui-hide-components-selected']
    this.tech().videoContainer().classList.add(...className);
  }

  commandUiHideComponentsAll() {
    const className = ['embed-ui-hide-components'];
    this.tech().videoContainer().classList.add(...className);
  }

  commandUiShowComponents() {
    this.tech().videoContainer().classList.remove('embed-ui-hide-components', 'embed-ui-hide-components-selected', 'embed-play-components-only');
  }

  commandUiShowPlayComponentsOnly() {
    this.tech().videoContainer().classList.add('embed-play-components-only');
  }

  commandUiHideVidioLogo() {
    this.tech().videoContainer().classList.add('embed-ui-hide-vidio-logo');
  }

  commandUiCancelHideVidioLogo() {
    this.tech().videoContainer().classList.remove('embed-ui-hide-vidio-logo');
  }

  commandSendEndEvent() {
    this.tech().on('ended', () => {
      const properties = this.buildProperties();
      const message = {
        event: 'vidio.playback.vod.ended',
        properties: properties
      };
      window.parent.postMessage(message, '*');
    });
  }

  commandSendStartEvent() {
    const properties = this.buildProperties();
    const message = {
      event: 'vidio.playback.ready',
      properties: properties
    };
    window.parent.postMessage(message, '*');
  }

  commandSendOnPlayEvent() {
    this.tech().on('play', () => {
      const properties = this.buildProperties();
      const message = {
        event: 'vidio.playback.statechange',
        state: {
          paused: false
        },
        properties: properties
      };
      window.parent.postMessage(message, '*');
    });
  }

  commandSendOnPauseEvent() {
    this.tech().on('pause', () => {
      const properties = this.buildProperties();
      const message = {
        event: 'vidio.playback.statechange',
        state: {
          paused: true
        },
        properties: properties
      };
      window.parent.postMessage(message, '*');
    });
  }

  commandSendOnMuteEvent() {
    this.tech().on('volumechange', () => {
      if(this.tech().muted()){
        this._lastMutedCondition = true;

        const properties = this.buildProperties();
        const message = {
          event: 'vidio.playback.statechange',
          state: {
            muted: true
          },
          properties: properties
        };
        window.parent.postMessage(message, '*');
      }
    });
  }

  commandSendOnUnmuteEvent() {
    this.tech().on('volumechange', () => {
      if(this.changedFromMuteToUnmute()){
        this._lastMutedCondition = false;

        const properties = this.buildProperties();
        const message = {
          event: 'vidio.playback.statechange',
          state: {
            muted: false
          },
          properties: properties
        };
        window.parent.postMessage(message, '*');
      }
    });
  }

  commandSendPlayEvent() {
    this.tech().one('playing', () => {
      const playlist = this.tech().hlsPlaylistMedia();
      let duration;

      if (this.tech().plugin().dashObject().hasDashObject()) {
        duration = parseInt(this.tech().currentTime());
      } else {
        duration = playlist.mediaSequence * playlist.targetDuration;
      }

      const properties = this.buildProperties({
        livestreaming_duration: duration
      });

      const message = {
        event: 'vidio.playback.playing',
        properties: properties
      };
      window.parent.postMessage(message, '*');
    });
  }

  commandSendStreamEndEvent() {
    this.tech().on('waiting', () => {
      if (this.playlists().error && this.playlists().error.status === 404) {
        const properties = this.buildProperties();
        const message = {
          event: 'vidio.playback.livestream.ended',
          properties: properties
        };
        window.parent.postMessage(message, '*');
      }
    });
  }

  commandSendOnAdStartEvent() {
    this.tech().on("adstarted", () => {
      const properties = this.buildProperties();
      const message = {
        event: "vidio.playback.adstart",
        properties: properties
      };
      window.parent.postMessage(message, '*');
    });
  }

  commandSendOnAdCompleteEvent() {
    this.tech().on("adcomplete", () => {
      const properties = this.buildProperties();
      const message = {
        event: "vidio.playback.adcomplete",
        properties: properties
      };
      window.parent.postMessage(message, '*');
    });
  }

  bindLivestreamingCCU() {
    const topic = TopicAdapter.subscribe('livestreaming/ccu', this.ccuCallback());
    this.removeTopicOnPlaybackRestart(topic);
  }

  ccuCallback() {
    return function (e) {
      e = (typeof e !== 'number') ? 0 : e;

      const properties = this.buildProperties({
        concurrent_users: e
      });
      const message = {
        event: 'vidio.playback.livestream',
        properties: properties
      };
      window.parent.postMessage(message, '*');
    }.bind(this)
  }

  removeTopicOnPlaybackRestart(topic) {
    this.tech().on('vp-restart', () => {
      topic.remove();
    });
  }

  changedFromMuteToUnmute(){
    return !this.tech().muted() && this.lastMutedCondition();
  }

  buildProperties(properties = {}) {
    return Object.assign({}, properties, {
      video_id: this.data().videoId(),
      query_params: URLUtils.params()
    });
  }
}
